import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Button,
    Typography,
    TextField,
    Box,
    LinearProgress,
    IconButton,
    Grid,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { HighlightOff } from '@mui/icons-material';
import { aiEnrichAttribute, AiConversationMessage } from '../../services/ai_service';
import { HtmlPreview } from '../../../../components/EnhancedRte';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiPaper-rounded": {
        borderRadius: "12px",
        padding: "16px"
    },
    "& .MuiDialogTitle-root": {
        padding: 0
    },
    "& .MuiDialogContent-root": {
        padding: 0
    }
}));

interface ChatBubbleProps {
    isUser: boolean;
}

const ChatBubble = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isUser',
})<ChatBubbleProps>(({ theme, isUser }) => ({
    maxWidth: '80%',
    padding: '10px 14px',
    borderRadius: '12px',
    marginBottom: '8px',
    alignSelf: isUser ? 'flex-end' : 'flex-start',
    backgroundColor: isUser ? theme.palette.primary.main : theme.palette.grey[100],
    color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
    textAlign: 'left',
    wordBreak: 'break-word',
}));

const BubbleLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.75rem',
    fontWeight: 600,
    marginBottom: '2px',
    opacity: 0.7,
}));

/**
 * AIEnrichmentDialogV2 — Chat-bubble conversation design.
 *
 * Same props interface as V1 (drop-in replacement):
 * - open, onClose, attributeName, currentAttributes, onApply, initialValue, attributeMetadata
 */
interface AIEnrichmentDialogV2Props {
    open: boolean;
    onClose: () => void;
    attributeName: string;
    currentAttributes: Record<string, unknown>;
    onApply: (attributeName: string, value: string | string[], meta: { sessionId: string | null; messages: AiConversationMessage[] }) => void;
    initialValue?: string | string[];
    attributeMetadata?: { Type?: string; Editor?: string; Options?: string[]; [key: string]: unknown };
}

function AIEnrichmentDialogV2(props: AIEnrichmentDialogV2Props) {
    const {
        open,
        onClose,
        attributeName,
        currentAttributes,
        onApply,
        initialValue = '',
        attributeMetadata = {}
    } = props;
    const theme = useTheme();
    const chatEndRef = useRef<HTMLDivElement | null>(null);

    const [enrichedValue, setEnrichedValue] = useState(initialValue);
    const [feedbackText, setFeedbackText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [messages, setMessages] = useState([]);
    const [sessionId, setSessionId] = useState(null);
    const [error, setError] = useState(null);

    // Chat history for rendering bubbles: { role: 'ai'|'user', content: string }
    const [chatHistory, setChatHistory] = useState([]);

    const getAttributeDisplayName = () => {
        const formatName = (str) => {
            if (str.includes('_')) {
                return str
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(' ');
            }
            return str
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, (str) => str.toUpperCase())
                .trim();
        };
        return formatName(attributeName);
    };

    useEffect(() => {
        if (open && !isLoading) {
            generateEnrichment();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatHistory, isLoading]);

    const generateEnrichment = async (withFeedback = false) => {
        setIsLoading(true);
        setError(null);

        // Add user message to chat history if this is a regeneration with feedback
        if (withFeedback && feedbackText.trim()) {
            setChatHistory(prev => [...prev, { role: 'user', content: feedbackText.trim() }]);
        }

        try {
            const feedback = withFeedback && feedbackText.trim() ? feedbackText : null;

            const result = await aiEnrichAttribute(
                attributeName,
                currentAttributes,
                messages,
                sessionId,
                attributeMetadata,
                feedback
            );

            if (result.success) {
                setEnrichedValue(result.value);
                setMessages(result.messages);
                setSessionId(result.sessionId);

                // Format the AI response for the chat bubble
                let aiContent = result.value;
                if (Array.isArray(aiContent)) {
                    aiContent = aiContent.join(', ');
                }
                if (result.originalSuggestion && result.originalSuggestion !== result.value) {
                    aiContent += `\n(AI suggested: "${result.originalSuggestion}" → Matched to: "${result.value}")`;
                }

                setChatHistory(prev => [...prev, { role: 'ai', content: aiContent }]);
            } else {
                setError(result.error || 'Failed to generate content');
            }
        } catch (err) {
            console.error('AI enrichment error:', err);
            setError('An error occurred while generating content');
        } finally {
            setIsLoading(false);
            setFeedbackText('');
        }
    };

    const handleSend = () => {
        if (feedbackText.trim()) {
            generateEnrichment(true);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleAccept = () => {
        if (enrichedValue) {
            onApply(attributeName, enrichedValue, {
                sessionId,
                messages
            });
        }
    };

    const handleCancel = () => {
        onClose();
    };

    return (
        <StyledDialog
            open={open}
            onClose={handleCancel}
            maxWidth="sm"
            fullWidth
            disablePortal
        >
            <DialogTitle>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography style={{ fontSize: "20px", fontWeight: 600 }}>
                            Logicbroker Intelligence
                        </Typography>
                        <Typography variant="body2" color="textSecondary" style={{ textAlign: 'left' }}>
                            Enriching: {getAttributeDisplayName()}
                        </Typography>
                    </Box>
                    <IconButton
                        style={{ height: "20px", width: "20px" }}
                        onClick={handleCancel}
                        size="large"
                    >
                        <HighlightOff style={{ color: "red" }} />
                    </IconButton>
                </Grid>
            </DialogTitle>
            <DialogContent>
                {/* Chat Area */}
                <Box style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '200px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    padding: '16px 0',
                    marginTop: '8px',
                }}>
                    {chatHistory.map((msg, index) => (
                        <ChatBubble key={index} isUser={msg.role === 'user'}>
                            <BubbleLabel>
                                {msg.role === 'user' ? 'You' : 'AI'}
                            </BubbleLabel>
                            {msg.role === 'ai' && attributeMetadata.Editor === 'html' ? (
                                <HtmlPreview value={msg.content} />
                            ) : (
                                <Typography style={{
                                    fontSize: '0.875rem',
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.5,
                                }}>
                                    {msg.content}
                                </Typography>
                            )}
                        </ChatBubble>
                    ))}
                    {isLoading && (
                        <Box style={{ padding: '8px 0' }}>
                            <LinearProgress />
                            <Typography variant="caption" color="textSecondary" style={{ marginTop: '4px', display: 'block' }}>
                                {chatHistory.length > 0 ? 'Regenerating...' : `Generating ${getAttributeDisplayName()}...`}
                            </Typography>
                        </Box>
                    )}
                    {error && (
                        <Box style={{ textAlign: 'center', padding: '8px 0' }}>
                            <Typography variant="body2" color="error">{error}</Typography>
                            <Button variant="text" size="small" onClick={() => generateEnrichment()} style={{ marginTop: '4px' }}>
                                Try Again
                            </Button>
                        </Box>
                    )}
                    <div ref={chatEndRef} />
                </Box>

                {/* Input Bar */}
                <Box style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'flex-end',
                    borderTop: `1px solid ${theme.palette.divider}`,
                    paddingTop: '12px',
                }}>
                    <TextField
                        fullWidth
                        multiline
                        maxRows={3}
                        variant="outlined"
                        size="small"
                        placeholder="Type feedback to refine..."
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isLoading}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSend}
                        disabled={isLoading || !feedbackText.trim()}
                        style={{ minWidth: '60px' }}
                    >
                        Send
                    </Button>
                </Box>

                {/* Action Buttons */}
                <Grid container justifyContent="flex-end" style={{ gap: "8px", marginTop: "12px" }}>
                    <Button variant="text" onClick={handleCancel} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="text"
                        onClick={handleAccept}
                        disabled={isLoading || !enrichedValue}
                    >
                        Accept
                    </Button>
                </Grid>
            </DialogContent>
        </StyledDialog>
    );
}

export default AIEnrichmentDialogV2;
